import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { monitoringAPI, serverAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import UrlStatusPanel from './UrlStatusPanel';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface HealthCheck {
  id: number;
  url: string;
  status: 'up' | 'down' | 'warning';
  response_time: number;
  status_code: number;
  checked_at: string;
  error_message?: string | null;
  warning_message?: string | null;
}

interface UptimePoint {
  time: string;
  status: 'up' | 'down' | 'warning';
  response_time: number;
  status_code: number;
  url: string;
  error_message?: string | null;
  warning_message?: string | null;
}

interface DowntimeIncident {
  id: number;
  url: string;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number;
  is_ongoing: boolean;
  is_major: boolean;
  total_failed_checks: number;
  error_message: string | null;
  notifications_sent?: number;
}

interface DowntimeSummary {
  date: string;
  incident_count: number;
  major_incidents: number;
  total_downtime_minutes: number;
  affected_urls: string[];
}

interface ServerCard {
  id: number;
  server_id: string;
  name: string;
  asset_name: string;
  os: string;
  status: string;
  public_ip: string | null;
  hostname: string | null;
  last_heartbeat_at: string | null;
  current_metrics: {
    cpu_usage_percent: number | null;
    memory_usage_percent: number | null;
    disk_metrics: Array<{ path?: string; mount?: string; usage_percent?: number; percent?: number; total_gb: number; used_gb: number }> | null;
    network_rx_bytes: number | null;
    network_tx_bytes: number | null;
    process_count: number | null;
  } | null;
}

interface PortResult {
  port: number;
  open: boolean;
  latency_ms: number | null;
}

interface MonitoredEndpoint {
  url: string;
  type: 'api' | 'website';
  status: 'up' | 'down' | 'warning' | 'unknown';
  status_code: number | null;
  response_time: number | null;
  error_message: string | null;
  warning_message: string | null;
  last_checked: string | null;
}

interface ContentScanEntry {
  url: string;
  content_length: number | null;
  error_count: number;
  has_errors: boolean;
  error_type: string | null;
  error_summary: string | null;
  content_sample: string | null;
  prev_content_length: number | null;
  size_change_pct: number | null;
  change_type: 'new' | 'none' | 'minor' | 'major' | 'error' | 'waf_blocked';
  scanned_at: string;
}

interface ContentScanUrl {
  url: string;
  latest: ContentScanEntry;
  history: ContentScanEntry[];
}

type ViewTab = 'live' | 'downtime' | 'major' | 'servers' | 'endpoints';

// Map HTTP status code to label
const httpStatusLabel = (code: number | null | undefined): string => {
  if (!code) return '';
  const labels: Record<number, string> = {
    200: 'OK', 201: 'Created', 204: 'No Content', 301: 'Moved', 302: 'Found',
    304: 'Not Modified', 400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden',
    404: 'Not Found', 405: 'Method Not Allowed', 408: 'Timeout', 409: 'Conflict',
    429: 'Too Many Requests', 500: 'Server Error', 502: 'Bad Gateway',
    503: 'Unavailable', 504: 'Gateway Timeout', 521: 'CF: Web server down',
    522: 'CF: Connection timeout', 523: 'CF: Origin unreachable', 524: 'CF: Timeout',
  };
  return labels[code] || String(code);
};

const StatusCodeBadge: React.FC<{ code: number | null | undefined }> = ({ code }) => {
  if (!code) return null;
  const color = code < 300 ? 'success' : code < 400 ? 'info' : code < 500 ? 'warning' : 'danger';
  return (
    <span className={`badge bg-${color}-subtle text-${color} border border-${color}-subtle ms-1`} style={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
      {code} {httpStatusLabel(code)}
    </span>
  );
};

const LiveMonitoringPage: React.FC = () => {
  useDocumentTitle('Live Monitoring');
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ViewTab>('live');

  // Live monitoring data
  const [checks, setChecks] = useState<HealthCheck[]>([]);
  const [uptimeData, setUptimeData] = useState<UptimePoint[]>([]);
  const [selectedCheck, setSelectedCheck] = useState<UptimePoint | null>(null);
  const [stats, setStats] = useState({
    uptime_percentage: 0,
    total_checks: 0,
    successful_checks: 0,
    failed_checks: 0,
    avg_response_time: 0,
  });

  // Downtime data
  const [downtimeSummary, setDowntimeSummary] = useState<DowntimeSummary[]>([]);
  const [downtimeTotals, setDowntimeTotals] = useState({
    total_incidents: 0,
    total_major_incidents: 0,
    total_downtime_minutes: 0,
  });

  // Major downtimes
  const [majorDowntimes, setMajorDowntimes] = useState<DowntimeIncident[]>([]);
  const [majorGrouped, setMajorGrouped] = useState<{
    '30-60min': DowntimeIncident[];
    '1-2hr': DowntimeIncident[];
    '2-4hr': DowntimeIncident[];
    '4hr+': DowntimeIncident[];
  }>({ '30-60min': [], '1-2hr': [], '2-4hr': [], '4hr+': [] });

  const [dayPeriod, setDayPeriod] = useState(30);

  // Servers tab state
  const [servers, setServers] = useState<ServerCard[]>([]);
  const [serversLoading, setServersLoading] = useState(false);
  const [portResults, setPortResults] = useState<Record<number, PortResult[]>>({});
  const [portChecking, setPortChecking] = useState<Set<number>>(new Set());

  // Endpoints tab state
  const [endpoints, setEndpoints] = useState<MonitoredEndpoint[]>([]);
  const [endpointsLoading, setEndpointsLoading] = useState(false);
  const [newEndpointUrl, setNewEndpointUrl] = useState('');
  const [addingEndpoint, setAddingEndpoint] = useState(false);

  // Content scans state
  const [contentScans, setContentScans] = useState<ContentScanUrl[]>([]);
  const [contentScanStats, setContentScanStats] = useState({ total_scans: 0, error_scans: 0, major_change_scans: 0 });
  const [selectedScan, setSelectedScan] = useState<ContentScanEntry | null>(null);
  const [exemptedUrls, setExemptedUrls] = useState<Set<string>>(new Set());
  const [exemptingUrl, setExemptingUrl] = useState<string | null>(null);
  const [scanTriggering, setScanTriggering] = useState(false);
  const [exemptReason, setExemptReason] = useState('');

  useEffect(() => {
    if (currentAsset) {
      loadData();
      const interval = setInterval(loadData, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [currentAsset, dayPeriod]);

  const loadData = async () => {
    if (!currentAsset) return;

    setLoading(true);
    // Also refresh servers (scoped to current asset)
    serverAPI.list({ asset_id: currentAsset.id }).then(res => {
      if (res.data.status === 'success') setServers(res.data.data.servers || []);
    }).catch(() => {});

    // Load content scans + exemptions separately (non-blocking)
    Promise.all([
      monitoringAPI.getContentScans(currentAsset.id).catch(() => null),
      monitoringAPI.getContentExemptions(currentAsset.id).catch(() => null),
    ]).then(([scansRes, exRes]: any[]) => {
      if (scansRes?.data?.status === 'success') {
        setContentScans(scansRes.data.data.urls || []);
        setContentScanStats({ total_scans: scansRes.data.data.total_scans, error_scans: scansRes.data.data.error_scans, major_change_scans: scansRes.data.data.major_change_scans });
      }
      if (exRes?.data?.status === 'success') {
        const urlArr: string[] = (exRes.data.data || []).map((e: any) => e.url);
        setExemptedUrls(new Set(urlArr));
      }
    });

    try {
      const [logsRes, summaryRes, majorRes] = await Promise.all([
        monitoringAPI.getHealthLog(currentAsset.id, 24),
        monitoringAPI.getDowntimeSummary(currentAsset.id, dayPeriod),
        monitoringAPI.getMajorDowntimes(currentAsset.id, 90),
      ]);

      if (logsRes.data.status === 'success') {
        const data = logsRes.data.data;
        setChecks(data.recent_checks || []);
        setUptimeData(data.uptime_timeline || []);
        setStats({
          uptime_percentage: data.uptime_percentage ?? 100,
          total_checks: data.total_checks || 0,
          successful_checks: data.successful_checks || 0,
          failed_checks: data.failed_checks || 0,
          avg_response_time: data.avg_response_time || 0,
        });
      }

      if (summaryRes.data.status === 'success') {
        const data = summaryRes.data.data;
        setDowntimeSummary(data.summary || []);
        setDowntimeTotals(data.totals || { total_incidents: 0, total_major_incidents: 0, total_downtime_minutes: 0 });
      }

      if (majorRes.data.status === 'success') {
        const data = majorRes.data.data;
        setMajorDowntimes(data.all || []);
        setMajorGrouped(data.grouped || { '30-60min': [], '1-2hr': [], '2-4hr': [], '4hr+': [] });
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load monitoring data:', error);
        toast.error('Failed to load monitoring data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadServers = async () => {
    if (!currentAsset) return;
    setServersLoading(true);
    try {
      const res = await serverAPI.list({ asset_id: currentAsset.id });
      if (res.data.status === 'success') {
        setServers(res.data.data.servers || []);
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        toast.error('Failed to load servers');
      }
    } finally {
      setServersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'servers') loadServers();
    if (activeTab === 'endpoints') loadEndpoints();
  }, [activeTab, currentAsset]);

  const loadEndpoints = async () => {
    if (!currentAsset) return;
    setEndpointsLoading(true);
    try {
      const res = await monitoringAPI.getEndpoints(currentAsset.id);
      if (res.data.status === 'success') setEndpoints(res.data.data?.endpoints || []);
    } catch { toast.error('Failed to load endpoints'); }
    finally { setEndpointsLoading(false); }
  };

  const handleAddEndpoint = async () => {
    if (!currentAsset || !newEndpointUrl.trim()) return;
    setAddingEndpoint(true);
    try {
      await monitoringAPI.addEndpoint(currentAsset.id, newEndpointUrl.trim());
      toast.success('Endpoint added — will be checked in the next cron cycle');
      setNewEndpointUrl('');
      loadEndpoints();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add endpoint');
    } finally { setAddingEndpoint(false); }
  };

  const handleRemoveEndpoint = async (url: string) => {
    if (!currentAsset) return;
    if (!window.confirm(`Remove "${url}" from monitoring?`)) return;
    try {
      await monitoringAPI.removeEndpoint(currentAsset.id, url);
      toast.success('Endpoint removed');
      setEndpoints(prev => prev.filter(e => e.url !== url));
    } catch { toast.error('Failed to remove endpoint'); }
  };

  const handlePortCheck = async (server: ServerCard) => {
    setPortChecking(prev => new Set(prev).add(server.id));
    try {
      const res = await serverAPI.checkPorts(server.id);
      if (res.data.status === 'success') {
        setPortResults(prev => ({ ...prev, [server.id]: res.data.data.ports }));
      }
    } catch {
      toast.error('Port check failed');
    } finally {
      setPortChecking(prev => { const s = new Set(prev); s.delete(server.id); return s; });
    }
  };

  const getMaxDiskUsage = (metrics: ServerCard['current_metrics']) => {
    if (!metrics?.disk_metrics?.length) return null;
    return Math.max(...metrics.disk_metrics.map(d => d.usage_percent ?? d.percent ?? 0));
  };

  const formatBytes = (bytes: number | null) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'danger';
      case 'degraded': return 'warning';
      default: return 'secondary';
    }
  };

  const getMetricColor = (value: number | null) => {
    if (value == null) return 'secondary';
    if (value >= 90) return 'danger';
    if (value >= 75) return 'warning';
    return 'success';
  };

  const formatHeartbeat = (ts: string | null) => {
    if (!ts) return 'Never';
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  };

  const getUptimeColor = () => {
    if (stats.uptime_percentage >= 99) return 'text-success';
    if (stats.uptime_percentage >= 95) return 'text-warning';
    return 'text-danger';
  };

  const chartData = uptimeData.map((point) => ({
    time: formatTime(point.time),
    response_time: point.response_time,
    status: point.status === 'up' ? 1 : 0,
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading monitoring data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1">Live Monitoring</h1>
            <p className="text-muted mb-0">Real-time uptime tracking and downtime incidents</p>
          </div>
          <div className="d-flex gap-2">
            <a className="btn btn-sm btn-outline-secondary" href="/endpoints/threats">
              <i className="bi bi-shield-exclamation me-1"></i>Endpoint Security
            </a>
            <button className="btn btn-sm btn-outline-primary" onClick={loadData}>
              <i className="bi bi-arrow-clockwise me-1"></i>Refresh
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'live' ? 'active' : ''}`} onClick={() => setActiveTab('live')}>
              <i className="bi bi-activity me-2"></i>Live Status
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'downtime' ? 'active' : ''}`} onClick={() => setActiveTab('downtime')}>
              <i className="bi bi-calendar-x me-2"></i>
              Downtime Days
              {downtimeSummary.length > 0 && <span className="badge bg-danger ms-2">{downtimeSummary.length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'major' ? 'active' : ''}`} onClick={() => setActiveTab('major')}>
              <i className="bi bi-exclamation-octagon me-2"></i>
              Major Outages
              {majorDowntimes.length > 0 && <span className="badge bg-danger ms-2">{majorDowntimes.length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'servers' ? 'active' : ''}`} onClick={() => setActiveTab('servers')}>
              <i className="bi bi-server me-2"></i>
              Servers
              {servers.length > 0 && <span className="badge bg-secondary ms-2">{servers.length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === 'endpoints' ? 'active' : ''}`} onClick={() => setActiveTab('endpoints')}>
              <i className="bi bi-hdd-network me-2"></i>
              Endpoints
              {endpoints.length > 0 && <span className="badge bg-secondary ms-2">{endpoints.length}</span>}
            </button>
          </li>
        </ul>

        {/* Live Status Tab */}
        {activeTab === 'live' && (
          <>
            {currentAsset && <UrlStatusPanel assetId={currentAsset.id} />}
            <div className="row mb-4 g-3">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className={`bg-${stats.uptime_percentage >= 99 ? 'success' : stats.uptime_percentage >= 95 ? 'warning' : 'danger'} bg-opacity-10 rounded-3 p-3`}>
                        <i className={`bi bi-graph-up-arrow ${getUptimeColor()} fs-4`}></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-1 small">Uptime</p>
                        <h4 className={`mb-0 fw-bold ${getUptimeColor()}`}>{stats.uptime_percentage.toFixed(2)}%</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                        <i className="bi bi-speedometer text-primary fs-4"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-1 small">Avg Response</p>
                        <h4 className="mb-0 fw-bold">{stats.avg_response_time.toFixed(0)}ms</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="bg-success bg-opacity-10 rounded-3 p-3">
                        <i className="bi bi-check-circle text-success fs-4"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-1 small">Successful</p>
                        <h4 className="mb-0 fw-bold text-success">{stats.successful_checks}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="bg-danger bg-opacity-10 rounded-3 p-3">
                        <i className="bi bi-x-circle text-danger fs-4"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-1 small">Failed</p>
                        <h4 className="mb-0 fw-bold text-danger">{stats.failed_checks}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {chartData.length > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-1">Response Time (Last 24 Hours)</h5>
                  <small className="text-muted">{uptimeData.length} health checks</small>
                </div>
                <div className="card-body">
                  <div style={{ height: '200px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorResp" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="time" tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} tickFormatter={(v) => `${v}ms`} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} formatter={(value) => [`${value}ms`, 'Response']} />
                        <Area type="monotone" dataKey="response_time" stroke="#3b82f6" fillOpacity={1} fill="url(#colorResp)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-1">Health Check Timeline</h5>
                <small className="text-muted">Click on any check for details</small>
              </div>
              <div className="card-body">
                {uptimeData.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-clock-history fs-1 d-block mb-3"></i>
                    <p className="mb-0">No health checks recorded yet</p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-1" style={{ minHeight: '50px' }}>
                      {uptimeData.map((point, index) => (
                        <div
                          key={index}
                          className="rounded"
                          style={{
                            width: uptimeData.length <= 20 ? '40px' : uptimeData.length <= 50 ? '24px' : '12px',
                            height: '40px',
                            backgroundColor: point.status === 'up' ? '#10b981' : point.status === 'warning' ? '#f59e0b' : '#ef4444',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            opacity: selectedCheck && selectedCheck !== point ? 0.4 : 1,
                            border: selectedCheck === point ? '2px solid #0d6efd' : 'none',
                          }}
                          title={`${formatDateTime(point.time)} - ${point.status.toUpperCase()}`}
                          onClick={() => setSelectedCheck(selectedCheck === point ? null : point)}
                        />
                      ))}
                    </div>
                    {selectedCheck && (
                      <div className={`mt-3 p-3 rounded border border-${selectedCheck.status === 'up' ? 'success' : selectedCheck.status === 'warning' ? 'warning' : 'danger'} bg-${selectedCheck.status === 'up' ? 'success' : selectedCheck.status === 'warning' ? 'warning' : 'danger'} bg-opacity-10`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="small w-100">
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                              <span className={`badge bg-${selectedCheck.status === 'up' ? 'success' : selectedCheck.status === 'warning' ? 'warning text-dark' : 'danger'}`}>
                                {selectedCheck.status === 'up' ? '✓ UP' : selectedCheck.status === 'warning' ? '⚠ WARNING' : '✗ DOWN'}
                              </span>
                              <StatusCodeBadge code={selectedCheck.status_code} />
                              <strong className="text-muted">{formatDateTime(selectedCheck.time)}</strong>
                            </div>
                            <div className="d-flex flex-column gap-1">
                              <div><i className="bi bi-link-45deg me-1 text-muted"></i><code className="bg-white bg-opacity-50 px-1 rounded small">{selectedCheck.url}</code></div>
                              <div><i className="bi bi-speedometer me-1 text-muted"></i><span>Response time: <strong>{selectedCheck.response_time}ms</strong></span></div>
                              {selectedCheck.error_message && (
                                <div className="text-danger"><i className="bi bi-x-circle me-1"></i>{selectedCheck.error_message}</div>
                              )}
                              {selectedCheck.warning_message && (
                                <div className="text-warning fw-semibold"><i className="bi bi-exclamation-triangle me-1"></i>{selectedCheck.warning_message}</div>
                              )}
                            </div>
                          </div>
                          <button className="btn btn-sm btn-outline-secondary ms-2 flex-shrink-0" onClick={() => setSelectedCheck(null)}><i className="bi bi-x"></i></button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">Recent Health Checks</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th className="border-0 ps-4">URL / Endpoint</th>
                        <th className="border-0 text-center">Status</th>
                        <th className="border-0 text-center">HTTP Code</th>
                        <th className="border-0 text-center">Response</th>
                        <th className="border-0 text-end pe-4">Checked At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checks.map((check) => (
                        <tr key={check.id} className={check.status === 'down' ? 'table-danger' : check.status === 'warning' ? 'table-warning' : ''}>
                          <td className="ps-4">
                            <code className="small bg-light px-2 py-1 rounded">{check.url}</code>
                            {check.warning_message && <div className="text-warning small mt-1"><i className="bi bi-exclamation-triangle-fill me-1"></i>{check.warning_message}</div>}
                            {check.error_message && check.status === 'down' && <div className="text-danger small mt-1"><i className="bi bi-x-circle me-1"></i>{check.error_message}</div>}
                          </td>
                          <td className="text-center">
                            <span className={`badge bg-${check.status === 'up' ? 'success' : check.status === 'warning' ? 'warning text-dark' : 'danger'}-subtle text-${check.status === 'up' ? 'success' : check.status === 'warning' ? 'warning' : 'danger'}`}>
                              {check.status === 'up' ? '✓ UP' : check.status === 'warning' ? '⚠ WARNING' : '✗ DOWN'}
                            </span>
                          </td>
                          <td className="text-center"><StatusCodeBadge code={check.status_code} /></td>
                          <td className="text-center">{check.response_time ? `${check.response_time}ms` : '—'}</td>
                          <td className="text-end pe-4 text-muted small">{formatDateTime(check.checked_at)}</td>
                        </tr>
                      ))}
                      {checks.length === 0 && (
                        <tr><td colSpan={4} className="text-center text-muted py-4">No recent checks available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Content Scans — PHP/language error detection + daily snapshot comparison */}
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">Content Scan History</h5>
                  <small className="text-muted">Scans for PHP, IIS, and server-side errors every 4 hours. Each URL is scanned when due.</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {contentScanStats.error_scans > 0 && (
                    <span className="badge bg-warning text-dark">
                      <i className="bi bi-exclamation-triangle-fill me-1"></i>
                      {contentScanStats.error_scans} error{contentScanStats.error_scans > 1 ? 's' : ''} found
                    </span>
                  )}
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={scanTriggering}
                    onClick={async () => {
                      setScanTriggering(true);
                      try {
                        await monitoringAPI.triggerContentScan(currentAsset!.id);
                        setTimeout(async () => {
                          const r = await monitoringAPI.getContentScans(currentAsset!.id);
                          if (r.data?.data?.urls) {
                            setContentScans(r.data.data.urls);
                            setContentScanStats({ total_scans: r.data.data.total_scans, error_scans: r.data.data.error_scans, major_change_scans: r.data.data.major_change_scans });
                          }
                          setScanTriggering(false);
                        }, 15000);
                      } catch { setScanTriggering(false); }
                    }}
                  >
                    {scanTriggering
                      ? <><span className="spinner-border spinner-border-sm me-1" />Scanning...</>
                      : <><i className="bi bi-arrow-clockwise me-1" />Scan Now</>}
                  </button>
                </div>
              </div>
              <div className="card-body">
                {contentScans.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-file-earmark-code fs-2 d-block mb-2"></i>
                    <p className="mb-1">No content scans yet</p>
                    <small>Click "Scan Now" or wait for the next scheduled scan</small>
                  </div>
                ) : (
                  <>
                    {contentScans.map((urlData) => (
                      <div key={urlData.url} className="mb-4">
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <code className="small bg-light px-2 py-1 rounded">{urlData.url}</code>
                          {urlData.latest.has_errors && (
                            <span className="badge bg-warning text-dark"><i className="bi bi-exclamation-triangle-fill me-1"></i>Errors Detected</span>
                          )}
                          {urlData.latest.change_type === 'major' && !urlData.latest.has_errors && (
                            <span className="badge text-white" style={{ background: '#f97316' }}>Major Change</span>
                          )}
                          {exemptedUrls.has(urlData.url) ? (
                            <button
                              className="btn btn-sm btn-outline-secondary py-0 ms-auto"
                              title="Remove exemption — resume email alerts for this URL"
                              onClick={async () => {
                                if (!currentAsset) return;
                                await monitoringAPI.removeContentExemption(currentAsset.id, urlData.url);
                                setExemptedUrls(prev => { const s = new Set(prev); s.delete(urlData.url); return s; });
                                toast.success('Exemption removed — alerts will resume');
                              }}
                            >
                              <i className="bi bi-bell-slash me-1"></i>Exempted · Remove
                            </button>
                          ) : (urlData.latest.has_errors || urlData.latest.change_type === 'major') && (
                            exemptingUrl === urlData.url ? (
                              <div className="d-flex gap-1 ms-auto">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Reason (optional)"
                                  style={{ width: '180px' }}
                                  value={exemptReason}
                                  onChange={e => setExemptReason(e.target.value)}
                                />
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={async () => {
                                    if (!currentAsset) return;
                                    await monitoringAPI.addContentExemption(currentAsset.id, urlData.url, exemptReason || undefined);
                                    setExemptedUrls(prev => { const s = new Set(Array.from(prev)); s.add(urlData.url); return s; });
                                    setExemptingUrl(null);
                                    setExemptReason('');
                                    toast.success('URL exempted — email alerts paused for this URL');
                                  }}
                                >Confirm</button>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setExemptingUrl(null); setExemptReason(''); }}>Cancel</button>
                              </div>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-warning ms-auto"
                                title="Pause email alerts for this URL (errors will still appear in dashboard)"
                                onClick={() => setExemptingUrl(urlData.url)}
                              >
                                <i className="bi bi-bell-slash me-1"></i>Exempt
                              </button>
                            )
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {urlData.history.map((scan, idx) => {
                            const color = scan.change_type === 'error' ? '#ef4444'
                              : scan.change_type === 'waf_blocked' ? '#6b7280'
                              : scan.change_type === 'major' ? '#f97316'
                              : '#10b981';
                            const label = scan.change_type === 'error' ? 'ERROR'
                              : scan.change_type === 'waf_blocked' ? 'WAF BLOCKED'
                              : scan.change_type === 'major' ? 'MAJOR CHANGE'
                              : scan.change_type === 'none' ? 'NO CHANGE'
                              : scan.change_type === 'new' ? 'NEW' : 'UPDATED';
                            return (
                              <div
                                key={idx}
                                className="rounded"
                                style={{
                                  width: urlData.history.length <= 20 ? '40px' : '24px',
                                  height: '36px',
                                  backgroundColor: color,
                                  cursor: 'pointer',
                                  opacity: selectedScan && selectedScan !== scan ? 0.4 : 1,
                                  border: selectedScan === scan ? '2px solid #0d6efd' : 'none',
                                  transition: 'all 0.2s',
                                }}
                                title={`${scan.scanned_at ? new Date(scan.scanned_at).toLocaleString() : ''} — ${label}`}
                                onClick={() => setSelectedScan(selectedScan === scan ? null : scan)}
                              />
                            );
                          })}
                        </div>
                        {selectedScan && urlData.history.includes(selectedScan) && (
                          <div className={`mt-2 p-3 rounded border ${selectedScan.has_errors ? 'border-warning bg-warning bg-opacity-10' : selectedScan.change_type === 'major' ? 'border-warning bg-warning bg-opacity-10' : 'border-success bg-success bg-opacity-10'}`}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="small">
                                <div className="d-flex gap-2 flex-wrap mb-2">
                                  <span className={`badge ${selectedScan.change_type === 'waf_blocked' ? 'bg-secondary' : selectedScan.has_errors ? 'bg-danger' : selectedScan.change_type === 'major' ? 'bg-warning text-dark' : 'bg-success'}`}>
                                    {selectedScan.change_type === 'waf_blocked' ? '🔒 WAF Blocked' : selectedScan.has_errors ? '⚠ Errors Found' : selectedScan.change_type === 'major' ? '⚠ Major Change' : selectedScan.change_type === 'none' ? '✓ No Change' : '✓ Updated'}
                                  </span>
                                  {selectedScan.error_type && <span className="badge bg-light text-dark border">{selectedScan.error_type.toUpperCase()}</span>}
                                  <span className="text-muted">{selectedScan.scanned_at ? new Date(selectedScan.scanned_at).toLocaleString() : ''}</span>
                                </div>
                                {selectedScan.error_summary && (
                                  <div className="text-danger fw-semibold mb-1"><i className="bi bi-exclamation-triangle-fill me-1"></i>{selectedScan.error_summary}</div>
                                )}
                                {selectedScan.error_count > 0 && (
                                  <div className="text-muted mb-1"><i className="bi bi-list-ul me-1"></i>{selectedScan.error_count} error occurrence{selectedScan.error_count > 1 ? 's' : ''} found on page</div>
                                )}
                                {selectedScan.content_sample && (
                                  <div className="mt-2">
                                    <div className="text-muted small mb-1"><i className="bi bi-code-slash me-1"></i>Error context (extracted from page):</div>
                                    <pre style={{ background: '#1e1e1e', color: '#f8f8f2', fontSize: '11px', padding: '10px', borderRadius: '6px', maxHeight: '180px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selectedScan.content_sample}</pre>
                                  </div>
                                )}
                                {selectedScan.size_change_pct !== null && selectedScan.size_change_pct !== 0 && (
                                  <div className={`mt-1 ${selectedScan.size_change_pct < -40 ? 'text-danger' : 'text-muted'}`}>
                                    <i className="bi bi-arrows-expand me-1"></i>
                                    Page size: {selectedScan.size_change_pct > 0 ? '+' : ''}{selectedScan.size_change_pct}% vs previous scan
                                    {selectedScan.content_length ? ` (${(selectedScan.content_length / 1024).toFixed(1)} KB)` : ''}
                                  </div>
                                )}
                                {selectedScan.change_type === 'waf_blocked' && (
                                  <div className="mt-2 text-muted"><i className="bi bi-shield-lock me-1"></i>Firewall is blocking the scanner. Whitelist your server IP in the site's WAF to enable content scanning.</div>
                                )}
                              </div>
                              <button className="btn btn-sm btn-outline-secondary ms-2 flex-shrink-0" onClick={() => setSelectedScan(null)}><i className="bi bi-x"></i></button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="text-muted small mt-2">
                      <i className="bi bi-info-circle me-1"></i>
                      Green = no errors / normal update · Orange = major size reduction · Red = errors found (PHP, IIS, Next.js etc.)
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Server Status Mini-Cards (only if servers registered) */}
            {servers.length > 0 && (
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-server me-2 text-primary"></i>
                    Server Status
                  </h5>
                  <a href="/servers" className="btn btn-sm btn-link text-decoration-none">View All</a>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {servers.map((server) => {
                      const cpu = server.current_metrics?.cpu_usage_percent ?? null;
                      const mem = server.current_metrics?.memory_usage_percent ?? null;
                      const disk = getMaxDiskUsage(server.current_metrics);
                      const rx = server.current_metrics?.network_rx_bytes ?? null;
                      const tx = server.current_metrics?.network_tx_bytes ?? null;
                      const statusColor = getStatusColor(server.status);
                      const isOnline = server.status === 'online';
                      return (
                        <div key={server.id} className="col-xl-3 col-md-4 col-sm-6">
                          <a href={`/servers/${server.id}`} className="text-decoration-none">
                            <div className={`card border-0 shadow-sm h-100 border-top border-3 border-${statusColor}`}>
                              <div className="card-body p-3">
                                {/* Server name & status */}
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="fw-semibold text-dark small text-truncate" style={{ maxWidth: '70%' }}>{server.name}</div>
                                  <span className={`badge bg-${statusColor} ms-1`} style={{ fontSize: '0.65rem' }}>
                                    {isOnline && <span className="me-1" style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#fff', animation: 'pulse 1.5s infinite' }}></span>}
                                    {server.status}
                                  </span>
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>{server.os} · {formatHeartbeat(server.last_heartbeat_at)}</div>

                                <hr className="my-2" />

                                {/* Metrics */}
                                <div style={{ fontSize: '0.72rem' }}>
                                  {[
                                    { label: 'CPU', value: cpu, icon: 'bi-cpu' },
                                    { label: 'RAM', value: mem, icon: 'bi-memory' },
                                    { label: 'Disk', value: disk, icon: 'bi-hdd' },
                                  ].map(({ label, value, icon }) => (
                                    <div key={label} className="mb-1">
                                      <div className="d-flex justify-content-between">
                                        <span className="text-muted"><i className={`bi ${icon} me-1`}></i>{label}</span>
                                        <span className={`fw-medium text-${getMetricColor(value)}`}>{value != null ? `${value.toFixed(1)}%` : '—'}</span>
                                      </div>
                                      <div className="progress rounded-pill" style={{ height: '3px' }}>
                                        <div className={`progress-bar bg-${getMetricColor(value)} rounded-pill`} style={{ width: `${value ?? 0}%` }} />
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Network */}
                                {(rx != null || tx != null) && (
                                  <div className="mt-2 pt-2 border-top d-flex justify-content-between text-muted" style={{ fontSize: '0.7rem' }}>
                                    <span><i className="bi bi-arrow-down-short text-success"></i>{formatBytes(rx)}</span>
                                    <span><i className="bi bi-arrow-up-short text-danger"></i>{formatBytes(tx)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Downtime Days Tab */}
        {activeTab === 'downtime' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="mb-1">Days with Downtime</h5>
                <small className="text-muted">View days that had downtime incidents</small>
              </div>
              <div className="btn-group btn-group-sm">
                {[7, 30, 60, 90].map((days) => (
                  <button key={days} className={`btn ${dayPeriod === days ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setDayPeriod(days)}>{days}d</button>
                ))}
              </div>
            </div>

            <div className="row mb-4 g-3">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 border-start border-danger border-3">
                  <div className="card-body">
                    <p className="text-muted mb-1 small">Total Incidents</p>
                    <h3 className="mb-0 text-danger">{downtimeTotals.total_incidents}</h3>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 border-start border-warning border-3">
                  <div className="card-body">
                    <p className="text-muted mb-1 small">Major Incidents (30m+)</p>
                    <h3 className="mb-0 text-warning">{downtimeTotals.total_major_incidents}</h3>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100 border-start border-info border-3">
                  <div className="card-body">
                    <p className="text-muted mb-1 small">Total Downtime</p>
                    <h3 className="mb-0 text-info">{formatDuration(downtimeTotals.total_downtime_minutes)}</h3>
                  </div>
                </div>
              </div>
            </div>

            {downtimeSummary.length === 0 ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-check-circle text-success fs-1"></i>
                  </div>
                  <h5 className="text-success mb-2">No Downtime Recorded</h5>
                  <p className="text-muted">Great news! No downtime incidents in the last {dayPeriod} days.</p>
                </div>
              </div>
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0 ps-4">Date</th>
                          <th className="border-0 text-center">Incidents</th>
                          <th className="border-0 text-center">Major</th>
                          <th className="border-0 text-center">Total Downtime</th>
                          <th className="border-0 text-end pe-4">Affected URLs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {downtimeSummary.map((day) => (
                          <tr key={day.date}>
                            <td className="ps-4">
                              <span className="fw-medium">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                            </td>
                            <td className="text-center"><span className="badge bg-danger">{day.incident_count}</span></td>
                            <td className="text-center">{day.major_incidents > 0 ? <span className="badge bg-warning text-dark">{day.major_incidents}</span> : <span className="text-muted">-</span>}</td>
                            <td className="text-center"><span className={day.total_downtime_minutes >= 30 ? 'text-danger fw-bold' : ''}>{formatDuration(day.total_downtime_minutes)}</span></td>
                            <td className="text-end pe-4">
                              {day.affected_urls.slice(0, 2).map((url, i) => (
                                <span key={i} className="badge bg-light text-dark me-1 small">{url.replace(/^https?:\/\//, '').slice(0, 25)}</span>
                              ))}
                              {day.affected_urls.length > 2 && <span className="text-muted small">+{day.affected_urls.length - 2}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Major Outages Tab */}
        {activeTab === 'major' && (
          <>
            <div className="mb-4">
              <h5 className="mb-1">Major Outages (30+ Minutes)</h5>
              <small className="text-muted">Grouped by duration severity</small>
            </div>

            {majorDowntimes.length === 0 ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-shield-check text-success fs-1"></i>
                  </div>
                  <h5 className="text-success mb-2">No Major Outages</h5>
                  <p className="text-muted">Excellent! No outages longer than 30 minutes in the last 90 days.</p>
                </div>
              </div>
            ) : (
              <div className="row g-4">
                {majorGrouped['4hr+'].length > 0 && (
                  <div className="col-12">
                    <div className="card border-0 shadow-sm border-start border-danger border-4">
                      <div className="card-header bg-danger bg-opacity-10 border-0 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 text-danger"><i className="bi bi-exclamation-octagon me-2"></i>Critical Outages (4+ hours)</h6>
                          <span className="badge bg-danger">{majorGrouped['4hr+'].length}</span>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="border-0 ps-4">URL</th>
                                <th className="border-0 text-center">Duration</th>
                                <th className="border-0 text-center">Started</th>
                                <th className="border-0 text-center">Status</th>
                                <th className="border-0 text-end pe-4">Notifications</th>
                              </tr>
                            </thead>
                            <tbody>
                              {majorGrouped['4hr+'].map((incident) => (
                                <tr key={incident.id}>
                                  <td className="ps-4"><code className="small bg-light px-2 py-1 rounded">{incident.url}</code></td>
                                  <td className="text-center"><span className="text-danger fw-bold">{formatDuration(incident.duration_minutes)}</span></td>
                                  <td className="text-center text-muted small">{formatDateTime(incident.started_at)}</td>
                                  <td className="text-center">{incident.is_ongoing ? <span className="badge bg-danger">ONGOING</span> : <span className="badge bg-success-subtle text-success">Resolved</span>}</td>
                                  <td className="text-end pe-4"><span className="badge bg-light text-dark">{incident.notifications_sent || 0} sent</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {majorGrouped['2-4hr'].length > 0 && (
                  <div className="col-12">
                    <div className="card border-0 shadow-sm border-start border-warning border-4">
                      <div className="card-header bg-warning bg-opacity-10 border-0 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 text-warning"><i className="bi bi-exclamation-triangle me-2"></i>Long Outages (2-4 hours)</h6>
                          <span className="badge bg-warning text-dark">{majorGrouped['2-4hr'].length}</span>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <div className="list-group list-group-flush">
                          {majorGrouped['2-4hr'].map((incident) => (
                            <div key={incident.id} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <code className="small bg-light px-2 py-1 rounded">{incident.url}</code>
                                  <span className="text-muted small ms-2">{formatDateTime(incident.started_at)}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="text-warning fw-bold">{formatDuration(incident.duration_minutes)}</span>
                                  {incident.is_ongoing && <span className="badge bg-danger">ONGOING</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {majorGrouped['1-2hr'].length > 0 && (
                  <div className="col-md-6">
                    <div className="card border-0 shadow-sm border-start border-info border-4 h-100">
                      <div className="card-header bg-info bg-opacity-10 border-0 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 text-info">1-2 Hour Outages</h6>
                          <span className="badge bg-info">{majorGrouped['1-2hr'].length}</span>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <div className="list-group list-group-flush">
                          {majorGrouped['1-2hr'].slice(0, 5).map((incident) => (
                            <div key={incident.id} className="list-group-item py-2">
                              <div className="d-flex justify-content-between small">
                                <span>{formatDateTime(incident.started_at)}</span>
                                <span className="text-info fw-medium">{formatDuration(incident.duration_minutes)}</span>
                              </div>
                            </div>
                          ))}
                          {majorGrouped['1-2hr'].length > 5 && <div className="list-group-item text-center text-muted small">+{majorGrouped['1-2hr'].length - 5} more</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {majorGrouped['30-60min'].length > 0 && (
                  <div className="col-md-6">
                    <div className="card border-0 shadow-sm border-start border-secondary border-4 h-100">
                      <div className="card-header bg-secondary bg-opacity-10 border-0 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 text-secondary">30-60 Minute Outages</h6>
                          <span className="badge bg-secondary">{majorGrouped['30-60min'].length}</span>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <div className="list-group list-group-flush">
                          {majorGrouped['30-60min'].slice(0, 5).map((incident) => (
                            <div key={incident.id} className="list-group-item py-2">
                              <div className="d-flex justify-content-between small">
                                <span>{formatDateTime(incident.started_at)}</span>
                                <span className="text-secondary fw-medium">{formatDuration(incident.duration_minutes)}</span>
                              </div>
                            </div>
                          ))}
                          {majorGrouped['30-60min'].length > 5 && <div className="list-group-item text-center text-muted small">+{majorGrouped['30-60min'].length - 5} more</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {/* Servers Tab */}
        {activeTab === 'servers' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="mb-1">Server Status</h5>
                <small className="text-muted">Live CPU, memory, disk and port health for all servers</small>
              </div>
              <button className="btn btn-sm btn-outline-primary" onClick={loadServers} disabled={serversLoading}>
                <i className={`bi bi-arrow-clockwise me-1 ${serversLoading ? 'spin' : ''}`}></i>Refresh
              </button>
            </div>

            {serversLoading ? (
              <div className="text-center py-5 text-muted">
                <div className="spinner-border text-primary mb-3" role="status" />
                <p>Loading servers...</p>
              </div>
            ) : servers.length === 0 ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="bi bi-server fs-1 text-muted d-block mb-3"></i>
                  <h5 className="text-muted mb-2">No Servers Registered</h5>
                  <p className="text-muted">Register a server to start monitoring it.</p>
                  <a href="/servers/register" className="btn btn-primary btn-sm">
                    <i className="bi bi-plus-circle me-1"></i>Register Server
                  </a>
                </div>
              </div>
            ) : (
              <div className="row g-3">
                {servers.map((server) => {
                  const cpu = server.current_metrics?.cpu_usage_percent ?? null;
                  const mem = server.current_metrics?.memory_usage_percent ?? null;
                  const disk = getMaxDiskUsage(server.current_metrics);
                  const ports = portResults[server.id];
                  const checking = portChecking.has(server.id);

                  return (
                    <div key={server.id} className="col-xl-4 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h6 className="mb-0 fw-semibold">{server.name}</h6>
                              <small className="text-muted">{server.public_ip || server.hostname || 'No IP'}</small>
                            </div>
                            <span className={`badge bg-${getStatusColor(server.status)}`}>
                              {server.status}
                            </span>
                          </div>

                          {/* Metrics */}
                          <div className="mb-3">
                            <div className="d-flex justify-content-between small mb-1">
                              <span className="text-muted"><i className="bi bi-cpu me-1"></i>CPU</span>
                              <span className={`text-${getMetricColor(cpu)}`}>{cpu != null ? `${cpu.toFixed(1)}%` : '—'}</span>
                            </div>
                            <div className="progress mb-2" style={{ height: '6px' }}>
                              <div className={`progress-bar bg-${getMetricColor(cpu)}`} style={{ width: `${cpu ?? 0}%` }} />
                            </div>

                            <div className="d-flex justify-content-between small mb-1">
                              <span className="text-muted"><i className="bi bi-memory me-1"></i>Memory</span>
                              <span className={`text-${getMetricColor(mem)}`}>{mem != null ? `${mem.toFixed(1)}%` : '—'}</span>
                            </div>
                            <div className="progress mb-2" style={{ height: '6px' }}>
                              <div className={`progress-bar bg-${getMetricColor(mem)}`} style={{ width: `${mem ?? 0}%` }} />
                            </div>

                            <div className="d-flex justify-content-between small mb-1">
                              <span className="text-muted"><i className="bi bi-hdd me-1"></i>Disk</span>
                              <span className={`text-${getMetricColor(disk)}`}>{disk != null ? `${disk.toFixed(1)}%` : '—'}</span>
                            </div>
                            <div className="progress" style={{ height: '6px' }}>
                              <div className={`progress-bar bg-${getMetricColor(disk)}`} style={{ width: `${disk ?? 0}%` }} />
                            </div>
                          </div>

                          {/* Last heartbeat */}
                          <div className="d-flex justify-content-between align-items-center small text-muted mb-3">
                            <span><i className="bi bi-clock me-1"></i>{formatHeartbeat(server.last_heartbeat_at)}</span>
                            <span className="text-uppercase" style={{ fontSize: '0.7rem' }}>{server.os}</span>
                          </div>

                          {/* Port check */}
                          {ports ? (
                            <div className="d-flex flex-wrap gap-1">
                              {ports.map((p) => (
                                <span
                                  key={p.port}
                                  className={`badge bg-${p.open ? 'success' : 'danger'}-subtle text-${p.open ? 'success' : 'danger'} border border-${p.open ? 'success' : 'danger'}`}
                                  title={p.open ? `Port ${p.port}: open (${p.latency_ms}ms)` : `Port ${p.port}: closed`}
                                >
                                  :{p.port} {p.open ? '✓' : '✗'}
                                </span>
                              ))}
                              <button
                                className="badge bg-light text-muted border ms-auto"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handlePortCheck(server)}
                                disabled={checking}
                              >
                                {checking ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-arrow-clockwise" />}
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-secondary w-100"
                              onClick={() => handlePortCheck(server)}
                              disabled={checking || !server.public_ip && !server.hostname}
                              title={!server.public_ip && !server.hostname ? 'No IP configured' : 'Check port reachability'}
                            >
                              {checking ? (
                                <><span className="spinner-border spinner-border-sm me-1" />Checking ports...</>
                              ) : (
                                <><i className="bi bi-diagram-3 me-1"></i>Check Ports</>
                              )}
                            </button>
                          )}
                        </div>
                        <div className="card-footer bg-transparent border-top-0 pt-0 pb-2 px-3">
                          <a href={`/servers/${server.id}`} className="btn btn-sm btn-link px-0 text-decoration-none text-muted small">
                            View details <i className="bi bi-arrow-right"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        {/* Endpoints Tab */}
        {activeTab === 'endpoints' && (
          <div>
            {/* Add Endpoint */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-1"><i className="bi bi-plus-circle text-primary me-2"></i>Add Monitoring Endpoint</h5>
                <small className="text-muted">Monitor any HTTP/HTTPS URL — website homepage, API health endpoint, status page, etc.</small>
              </div>
              <div className="card-body">
                <div className="d-flex gap-2">
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://api.example.com/health or https://example.com"
                    value={newEndpointUrl}
                    onChange={e => setNewEndpointUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddEndpoint()}
                  />
                  <button className="btn btn-primary px-4" onClick={handleAddEndpoint} disabled={addingEndpoint || !newEndpointUrl.trim()}>
                    {addingEndpoint ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-plus me-1"></i>Add</>}
                  </button>
                </div>
                <div className="mt-2 d-flex gap-3 flex-wrap">
                  <small className="text-muted"><i className="bi bi-globe me-1"></i>Websites</small>
                  <small className="text-muted"><i className="bi bi-cpu me-1"></i>REST APIs</small>
                  <small className="text-muted"><i className="bi bi-lightning me-1"></i>GraphQL endpoints</small>
                  <small className="text-muted"><i className="bi bi-heart-pulse me-1"></i>/health, /ping, /status</small>
                </div>
              </div>
            </div>

            {/* Endpoints List */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0"><i className="bi bi-hdd-network text-primary me-2"></i>Monitored Endpoints ({endpoints.length})</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={loadEndpoints} disabled={endpointsLoading}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
              </div>
              <div className="card-body p-0">
                {endpointsLoading ? (
                  <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary me-2"></div>Loading...</div>
                ) : endpoints.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-hdd-network fs-1 d-block mb-3 opacity-25"></i>
                    <p className="mb-1">No endpoints configured yet</p>
                    <small>Add your first endpoint above to start monitoring</small>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">Endpoint URL</th>
                          <th>Type</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">HTTP Code</th>
                          <th className="text-center">Response</th>
                          <th>Last Checked</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {endpoints.map((ep, i) => (
                          <tr key={i} className={ep.status === 'down' ? 'table-danger' : ep.status === 'warning' ? 'table-warning' : ''}>
                            <td className="ps-4">
                              <div className="d-flex align-items-center gap-2">
                                <i className={`bi ${ep.type === 'api' ? 'bi-cpu' : 'bi-globe'} text-muted`}></i>
                                <code className="small">{ep.url}</code>
                              </div>
                              {ep.warning_message && <div className="text-warning small mt-1"><i className="bi bi-exclamation-triangle me-1"></i>{ep.warning_message}</div>}
                              {ep.error_message && ep.status === 'down' && <div className="text-danger small mt-1"><i className="bi bi-x-circle me-1"></i>{ep.error_message}</div>}
                            </td>
                            <td>
                              <span className={`badge bg-${ep.type === 'api' ? 'primary' : 'info'}-subtle text-${ep.type === 'api' ? 'primary' : 'info'} border`} style={{ fontSize: '0.7rem' }}>
                                <i className={`bi ${ep.type === 'api' ? 'bi-cpu' : 'bi-globe'} me-1`}></i>{ep.type === 'api' ? 'API' : 'Website'}
                              </span>
                            </td>
                            <td className="text-center">
                              {ep.status === 'unknown' ? (
                                <span className="badge bg-secondary-subtle text-secondary border">Unknown</span>
                              ) : (
                                <span className={`badge bg-${ep.status === 'up' ? 'success' : ep.status === 'warning' ? 'warning text-dark' : 'danger'}-subtle text-${ep.status === 'up' ? 'success' : ep.status === 'warning' ? 'warning' : 'danger'} border border-${ep.status === 'up' ? 'success' : ep.status === 'warning' ? 'warning' : 'danger'}-subtle`}>
                                  {ep.status === 'up' ? '✓ UP' : ep.status === 'warning' ? '⚠ WARNING' : '✗ DOWN'}
                                </span>
                              )}
                            </td>
                            <td className="text-center"><StatusCodeBadge code={ep.status_code} /></td>
                            <td className="text-center text-muted small">{ep.response_time ? `${ep.response_time}ms` : '—'}</td>
                            <td className="text-muted small">{ep.last_checked ? formatDateTime(ep.last_checked) : 'Never'}</td>
                            <td className="pe-3">
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveEndpoint(ep.url)} title="Remove">
                                <i className="bi bi-trash3"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LiveMonitoringPage;
